import React, { useState, useMemo } from 'react';
import { DataTable, DataTableRowClickEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { PaymentMethodIcon } from './componenets/payment-method-icon';
import { PaymentDetailsTooltip } from './componenets/payment-details-tooltip';
import { PaymentSummary } from './componenets/payment-summary';
import { InstallmentSummary } from './componenets/installment-summary';
import {
  calculateInstallmentSummaries,
  calculateTotalInstallmentAmount,
} from './utils/calculate-installment-summaries';
import dateUtils from '../../../../../../utils/date-format';
import { FileDown, Undo2 } from 'lucide-react';
import {
  PaymentReferenceType,
  UserPayment,
  UserPaymentWithItemPrice,
  PaymentMethodsDb,
  PaymentCheckEntity,
  PaymentCreditCardDetailsEntity,
  PaymentScholarshipEntity,
} from '../../../../../../api/types/students/payment-services/user-payments/user-payment-service-with-price-list';
import { UserSummaryType } from '../../../../../../api/types/user/user-summary.type';
import { RefundDialog } from './dialogs/refund-dialog';
import { InstallmentWithPlanItemsDetails } from '../../../../../../api/types/payments/installments/entities';
import { InstallmentBankTransferWithPlanItemsDetails } from '../../../../../../api/types/students/user-details-student';
import { PaymentMethod } from '../../../payment-methods/payment-method-selection-dialog';

export interface PaymentServiceHistoryListProps {
  payments: UserPaymentWithItemPrice[];
  totalPrice: number;
  user: UserSummaryType;
  allocationId: number;
  creditCardInstallmentPlans?: InstallmentWithPlanItemsDetails[];
  bankTransferInstallmentPlans?: InstallmentBankTransferWithPlanItemsDetails[];
}

export const PaymentServiceHistoryList: React.FC<
  PaymentServiceHistoryListProps
> = ({
  payments,
  totalPrice,
  user,
  allocationId,
  creditCardInstallmentPlans = [],
  bankTransferInstallmentPlans = [],
}) => {
  const [expandedRows, setExpandedRows] = React.useState<UserPayment[]>([]);
  const [selectedPaymentForRefund, setSelectedPaymentForRefund] =
    useState<UserPayment | null>(null);
  const [isRefundDialogVisible, setIsRefundDialogVisible] = useState(false);

  type PaymentRow = UserPaymentWithItemPrice & {
    __parentPayment?: UserPaymentWithItemPrice;
    __check?: PaymentCheckEntity;
  };

  const tableRows: PaymentRow[] = useMemo(() => {
    return payments.flatMap((payment) => {
      if (
        payment.paymentMethodType === PaymentMethodsDb.Check &&
        Array.isArray(payment.checks) &&
        payment.checks.length > 0
      ) {
        return payment.checks.map((check) => ({
          ...payment,
          __parentPayment: payment,
          __check: check,
          itemAmount: check.itemAmount ?? 0,
        }));
      }

      return [
        {
          ...payment,
          __parentPayment: payment,
        },
      ];
    });
  }, [payments]);

  // Calculate installment plan summaries for this allocation
  const installmentSummaries = useMemo(
    () =>
      calculateInstallmentSummaries({
        allocationId,
        creditCardInstallmentPlans,
        bankTransferInstallmentPlans,
      }),
    [allocationId, creditCardInstallmentPlans, bankTransferInstallmentPlans],
  );

  const totalInstallmentAmount = useMemo(
    () => calculateTotalInstallmentAmount(installmentSummaries),
    [installmentSummaries],
  );

  const onRowClick = React.useCallback((e: DataTableRowClickEvent) => {
    const clickedRow = e.data as PaymentRow;
    const clickedPayment = (clickedRow.__parentPayment ??
      clickedRow) as UserPayment;
    setExpandedRows((prev) =>
      prev.some((p) => p.id === clickedPayment.id)
        ? prev.filter((p) => p.id !== clickedPayment.id)
        : [...prev, clickedPayment],
    );
  }, []);

  const handleRefundClick = (payment: UserPayment) => {
    setSelectedPaymentForRefund(payment);
    setIsRefundDialogVisible(true);
  };

  const handleRefundDialogClose = () => {
    setIsRefundDialogVisible(false);
    setSelectedPaymentForRefund(null);
  };

  // Helper function to convert PaymentMethodsDb to PaymentMethod
  const convertPaymentMethod = (method: PaymentMethodsDb): PaymentMethod => {
    switch (method) {
      case PaymentMethodsDb.CreditCard:
        return PaymentMethod.CreditCard;
      case PaymentMethodsDb.BankTransfer:
        return PaymentMethod.BankTransfer;
      case PaymentMethodsDb.Check:
        return PaymentMethod.Check;
      case PaymentMethodsDb.Scholarship:
        return PaymentMethod.Scholarship;
      case PaymentMethodsDb.Cash:
        return PaymentMethod.Cash;
      default:
        return PaymentMethod.CreditCard; // fallback
    }
  };

  const refundButtonTemplate = (rowData: UserPayment) => {
    // Only show refund button for payments with parentId null
    if (rowData.paymentReferenceType === PaymentReferenceType.REVERSAL) {
      return null;
    }

    // Refund is supported only for cash / credit card / bank transfer.
    const refundableMethods: PaymentMethodsDb[] = [
      PaymentMethodsDb.Cash,
      PaymentMethodsDb.CreditCard,
      PaymentMethodsDb.BankTransfer,
    ];
    if (!refundableMethods.includes(rowData.paymentMethodType)) {
      return null;
    }

    return (
      <button
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '0.5rem 1rem',
          border: '1px solid #E5E7EB',
          borderRadius: '0.375rem',
          backgroundColor: '#FEF3C7',
          color: '#92400E',
          fontSize: '0.875rem',
          fontWeight: 500,
          cursor: 'pointer',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleRefundClick(rowData);
        }}
      >
        <Undo2
          style={{
            width: '16px',
            height: '16px',
            marginRight: '0.5rem',
          }}
        />
        החזר תשלום
      </button>
    );
  };

  const rowExpansionTemplate = (payment: UserPayment) => (
    <PaymentDetailsTooltip payment={payment} />
  );

  return (
    <div className="payment-service-history">
      <InstallmentSummary
        installmentSummaries={installmentSummaries}
        totalInstallmentAmount={totalInstallmentAmount}
      />
      <PaymentSummary
        payments={payments}
        totalPrice={totalPrice}
        installmentAmount={totalInstallmentAmount}
      />

      <DataTable
        value={tableRows}
        expandedRows={expandedRows}
        onRowClick={onRowClick}
        /*rowExpansionTemplate={rowExpansionTemplate}*/
        style={{
          marginTop: '1rem',
          cursor: 'pointer',
        }}
        className="payment-history-table"
      >
        <Column
          field="id"
          header="מזהה תשלום"
          style={{ width: '100px', textAlign: 'right' }}
        />
        <Column
          field="paymentAmount"
          header="סכום"
          style={{ width: '150px', textAlign: 'right' }}
          body={(rowData: PaymentRow) => (
            <span style={{ color: '#00C853', fontWeight: 'bold' }}>
              {'\u202D'}₪{rowData.itemAmount}
              {'\u202C'}
            </span>
          )}
        />
        <Column
          field="paymentDate"
          header="תאריך תשלום"
          style={{ width: '150px', textAlign: 'right' }}
          body={(rowData: PaymentRow) => {
            const payment = rowData.__parentPayment ?? rowData;
            return dateUtils.displayFormatDate(payment.paymentDate);
          }}
        />
        <Column
          field="paymentMethodType"
          header="אמצעי תשלום"
          style={{ width: '200px', textAlign: 'right' }}
          body={(rowData: PaymentRow) => {
            const payment = rowData.__parentPayment ?? rowData;
            // Scholarship: display "מלגה (funderName)"
            if (
              payment.paymentMethodType === PaymentMethodsDb.Scholarship &&
              payment.paymentDetails
            ) {
              const scholarshipDetails =
                payment.paymentDetails as PaymentScholarshipEntity;
              const funderName = scholarshipDetails.funderName;
              return (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <i
                    className="pi pi-gift"
                    style={{ color: '#8B5CF6', fontSize: '1.2rem' }}
                  />
                  <span style={{ color: '#495057' }}>
                    {`מלגה${
                      funderName && funderName.trim().length > 0
                        ? ` (${funderName})`
                        : ''
                    }`}
                  </span>
                </div>
              );
            }

            // Check if it's credit card with installments
            if (
              payment.paymentMethodType === PaymentMethodsDb.CreditCard &&
              payment.paymentDetails &&
              payment.paymentDetails?.installmentNumber
            ) {
              const installmentNumber = (
                payment.paymentDetails as PaymentCreditCardDetailsEntity
              ).installmentNumber;
              return (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <i
                    className="pi pi-credit-card"
                    style={{ color: '#2196F3', fontSize: '1.2rem' }}
                  />
                  <span style={{ color: '#495057' }}>
                    כרטיס אשראי ({installmentNumber} תשלומים)
                  </span>
                </div>
              );
            }
            // Default: use PaymentMethodIcon
            return (
              <PaymentMethodIcon
                methodType={convertPaymentMethod(payment.paymentMethodType)}
              />
            );
          }}
        />

        <Column
          field="paymentMethodType"
          header="חשבונית"
          style={{ width: '200px', textAlign: 'right' }}
          body={(rowData: PaymentRow) => {
            const payment = rowData.__parentPayment ?? rowData;
            if (!payment.invoiceUrl) {
              return (
                <span style={{ color: '#6B7280' }}>לא מצורפת חשבונית</span>
              );
            }

            return (
              <button
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '0.5rem 1rem',
                  border: '1px solid #E5E7EB',
                  borderRadius: '0.375rem',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(payment.invoiceUrl, '_blank');
                }}
              >
                <FileDown
                  style={{
                    width: '16px',
                    height: '16px',
                    marginRight: '0.5rem',
                  }}
                />
                לחץ לצפייה
              </button>
            );
          }}
        />
        <Column
          field="paymentDetails"
          header="פרטים נוספים"
          style={{ width: '100px', textAlign: 'right' }}
          body={(rowData: PaymentRow) => {
            const parent = rowData.__parentPayment ?? rowData;
            // Per-check rows: parent.paymentDetails is always the first check from the API;
            // override with the row’s check so פרטים נוספים matches that row.
            const paymentForTooltip: UserPayment = rowData.__check
              ? { ...parent, paymentDetails: rowData.__check }
              : parent;
            return <PaymentDetailsTooltip payment={paymentForTooltip} />;
          }}
        />
        <Column
          header="החזר תשלום"
          style={{ width: '150px', textAlign: 'right' }}
          body={(rowData: PaymentRow) => {
            const payment = (rowData.__parentPayment ?? rowData) as UserPayment;
            return refundButtonTemplate(payment);
          }}
        />
      </DataTable>

      {selectedPaymentForRefund && (
        <RefundDialog
          visible={isRefundDialogVisible}
          payment={selectedPaymentForRefund}
          user={user}
          onClose={handleRefundDialogClose}
        />
      )}
    </div>
  );
};
